/**
 * The Customer Support module's permission vocabulary.
 *
 * This module is a HOME FOR TOOLS, not one screen, so the ladder is built to grow: the
 * floor (`view`) is "may open the support tools and look a customer up", and every
 * capability that CHANGES something upstream gets its own rung above it. Today there is
 * one such capability; a future refund or re-send tool adds a permission here rather
 * than reusing `eraseData`, because "may look up a customer" and "may destroy their
 * record in four SaaS platforms" are not the same job.
 *
 * `view` is deliberately a real permission and not granted to All Users (unlike the org
 * chart): contact lookup returns customer PII — name, email, phone — from HubSpot, and
 * that is not company-wide reference data.
 *
 * `eraseData` is the only permission in this repo behind which sits an IRREVERSIBLE,
 * cross-system destruction of a person's record. It is a rung of its own so that a
 * support agent can be given the module without it, and so that the access-control
 * catalogue prints that sentence to whoever is granting it.
 */
import { defineModulePermissions } from '@noissue/authz';

export const SUPPORT = defineModulePermissions({
  id: 'support',
  name: 'Customer Support',
  icon: 'life-buoy',
  permissions: {
    view: {
      label: 'Use the support tools',
      description:
        'Open Customer Support and look customers up in HubSpot, including their ' +
        'name, email and phone number.',
    },
    eraseData: {
      label: 'Erase customer data',
      description:
        'Permanently delete a customer from every connected system (HubSpot GDPR ' +
        'delete, Keycloak account deletion). Irreversible, not undone by a restore, ' +
        'and it blocklists the address in HubSpot — grant it only to whoever owns ' +
        'erasure requests.',
    },
  },
  visibility: 'view',
  levels: [
    { key: 'view', label: 'View', grants: ['view'] },
    { key: 'erase', label: 'Erase data', grants: ['view', 'eraseData'] },
  ],
});
